import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMedicalConditionsActive, QUERY_KEYS } from "@/hooks/use-queries";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Heart, HeartPulse, HeartHandshake, MapPin, Bell, CheckCircle2, ArrowRight, ArrowLeft,
  HandHeart, Stethoscope, Brain, Baby, Ribbon, Pill,
  Activity, Shield, Smile, Siren, Wind, Accessibility, Bone,
  User as UserIcon, Sparkles
} from "lucide-react";
import healthWalletLogo from "@assets/generated_images/healthwallet_shield_heart_logo.png";

const STEPS = [
  { id: "welcome", label: "Welcome", icon: Sparkles },
  { id: "profile", label: "Profile", icon: UserIcon },
  { id: "location", label: "Location", icon: MapPin },
  { id: "interests", label: "Interests", icon: Heart },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "complete", label: "Complete", icon: CheckCircle2 },
];

const DEFAULT_CONDITIONS = [
  { slug: "congenital-heart-disease", label: "Congenital Heart Disease", icon: HeartPulse, category: "cardiac" },
  { slug: "pediatric-cancer", label: "Pediatric Cancer & Blood Disorders", icon: Ribbon, category: "cancer" },
  { slug: "nicu-neonatal", label: "NICU & Neonatal Care", icon: Baby, category: "neonatal" },
  { slug: "epilepsy-neurology", label: "Epilepsy & Neurological Conditions", icon: Brain, category: "neurological" },
  { slug: "asthma-respiratory", label: "Asthma & Respiratory Conditions", icon: Wind, category: "respiratory" },
  { slug: "type-1-diabetes", label: "Type 1 Diabetes & Endocrine Disorders", icon: Activity, category: "endocrine" },
  { slug: "pediatric-mental-health", label: "Pediatric Mental Health", icon: Brain, category: "mental_health" },
  { slug: "cerebral-palsy", label: "Cerebral Palsy & Developmental Disabilities", icon: Accessibility, category: "developmental" },
  { slug: "kidney-disease", label: "Kidney Disease & Nephrology", icon: Stethoscope, category: "renal" },
  { slug: "orthopedic-spinal", label: "Orthopedic & Spinal Conditions", icon: Bone, category: "orthopedic" },
  { slug: "cleft-craniofacial", label: "Cleft & Craniofacial Conditions", icon: Smile, category: "craniofacial" },
  { slug: "fetal-prenatal", label: "Fetal & Prenatal Conditions", icon: HeartHandshake, category: "prenatal" },
  { slug: "trauma-emergency", label: "Pediatric Trauma & Emergency Care", icon: Siren, category: "trauma" },
];

export default function OnboardingPage() {
  usePageTitle("Welcome to HealthWallet", "Complete your HealthWallet profile setup to access community-powered healthcare funding.");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);

  const [profileData, setProfileData] = useState({
    bio: "",
    phone: "",
    zipCode: "",
    supportInterests: [] as string[],
    notifications: {
      fundUpdates: true,
      communityNews: true,
      impactReports: true,
      newConditions: false,
    },
  });

  const { data: dbConditions } = useMedicalConditionsActive();

  const conditions = dbConditions && dbConditions.length > 0
    ? dbConditions.map(c => ({
        slug: c.slug,
        label: c.name,
        icon: DEFAULT_CONDITIONS.find(dc => dc.slug === c.slug)?.icon || Heart,
        category: c.category,
      }))
    : DEFAULT_CONDITIONS;

  const updateProfileMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("PATCH", "/api/profile", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.profile });
    },
  });

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleInterest = (slug: string) => {
    setProfileData(prev => ({
      ...prev,
      supportInterests: prev.supportInterests.includes(slug)
        ? prev.supportInterests.filter(s => s !== slug)
        : [...prev.supportInterests, slug],
    }));
  };

  const handleComplete = async () => {
    try {
      await updateProfileMutation.mutateAsync({
        bio: profileData.bio || undefined,
        phone: profileData.phone || undefined,
        zipCode: profileData.zipCode || undefined,
        supportInterests: profileData.supportInterests,
        notificationPreferences: JSON.stringify(profileData.notifications),
        onboardingCompleted: true,
      });
      toast({
        title: "Welcome aboard!",
        description: "Your profile is all set. Let's start making an impact!",
      });
      setLocation("/");
    } catch {
      toast({
        title: "Something went wrong",
        description: "We couldn't save your profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSkip = async () => {
    try {
      await updateProfileMutation.mutateAsync({ onboardingCompleted: true });
      setLocation("/");
    } catch {
      setLocation("/");
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="onboarding-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <img
            src={healthWalletLogo}
            alt="HealthWallet"
            className="h-10 w-10 mx-auto mb-2 object-contain"
            data-testid="img-onboarding-logo"
          />
          <Progress value={progress} className="h-1.5 mb-4" data-testid="progress-onboarding" />
          <div className="flex justify-center gap-2">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                    i < currentStep
                      ? "bg-primary text-primary-foreground"
                      : i === currentStep
                      ? "bg-primary/20 text-primary border-2 border-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                  data-testid={`step-indicator-${step.id}`}
                >
                  {i < currentStep ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {currentStep === 0 && (
              <div className="text-center space-y-4" data-testid="step-welcome">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <HandHeart className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Welcome, {user?.firstName}!</CardTitle>
                <CardDescription className="text-base">
                  Thank you for joining HealthWallet as a Community Supporter.
                  Your contributions help families in the Twin Cities access
                  the healthcare they need.
                </CardDescription>
                <div className="space-y-2 text-left bg-muted/50 rounded-lg p-4">
                  <p className="text-sm font-medium">In the next few steps, you'll:</p>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <UserIcon className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      Set up your supporter profile
                    </li>
                    <li className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      Connect with your local community
                    </li>
                    <li className="flex items-center gap-2">
                      <Heart className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      Choose health conditions to support
                    </li>
                    <li className="flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                      Set your notification preferences
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4" data-testid="step-profile">
                <div className="text-center">
                  <CardTitle className="text-xl">Tell us about yourself</CardTitle>
                  <CardDescription>Help the community get to know you</CardDescription>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="bio">About you (optional)</Label>
                    <Textarea
                      id="bio"
                      placeholder="Share why you want to support healthcare in your community..."
                      value={profileData.bio}
                      onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                      className="mt-1 resize-none"
                      rows={3}
                      maxLength={500}
                      data-testid="input-onboarding-bio"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{profileData.bio.length}/500</p>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone number (optional)</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(612) 555-0100"
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1"
                      data-testid="input-onboarding-phone"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4" data-testid="step-location">
                <div className="text-center">
                  <CardTitle className="text-xl">Your location</CardTitle>
                  <CardDescription>
                    Help us connect you with community funds in your area
                  </CardDescription>
                </div>
                <div>
                  <Label htmlFor="zipCode">Zip code</Label>
                  <Input
                    id="zipCode"
                    placeholder="55401"
                    value={profileData.zipCode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, zipCode: e.target.value.replace(/\D/g, "").slice(0, 5) }))}
                    className="mt-1 text-lg tracking-wider"
                    maxLength={5}
                    data-testid="input-onboarding-zipcode"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    We serve the Minneapolis-St. Paul metro area. Your zip code helps us
                    show you community funds and healthcare needs near you.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4" data-testid="step-interests">
                <div className="text-center">
                  <CardTitle className="text-xl">What matters to you?</CardTitle>
                  <CardDescription>
                    Select health conditions you'd like to support. You can change these anytime.
                  </CardDescription>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {conditions.map((condition) => {
                    const Icon = condition.icon;
                    const isSelected = profileData.supportInterests.includes(condition.slug);
                    return (
                      <button
                        key={condition.slug}
                        onClick={() => toggleInterest(condition.slug)}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-muted hover-elevate"
                        }`}
                        data-testid={`interest-${condition.slug}`}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="text-sm font-medium">{condition.label}</span>
                      </button>
                    );
                  })}
                </div>
                {profileData.supportInterests.length > 0 && (
                  <p className="text-sm text-muted-foreground text-center">
                    {profileData.supportInterests.length} selected
                  </p>
                )}
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-4" data-testid="step-notifications">
                <div className="text-center">
                  <CardTitle className="text-xl">Stay informed</CardTitle>
                  <CardDescription>
                    Choose how you'd like to hear about community impact
                  </CardDescription>
                </div>
                <div className="space-y-3">
                  {[
                    { key: "fundUpdates" as const, label: "Fund updates", desc: "When funds you support receive contributions or reach goals" },
                    { key: "communityNews" as const, label: "Community news", desc: "Updates from local healthcare partners and community events" },
                    { key: "impactReports" as const, label: "Impact reports", desc: "Monthly reports on how your contributions are making a difference" },
                    { key: "newConditions" as const, label: "New condition funds", desc: "When new health condition funds are created in your area" },
                  ].map((item) => (
                    <div
                      key={item.key}
                      className="flex items-start justify-between gap-3 p-3 rounded-lg bg-muted/30"
                      data-testid={`notification-${item.key}`}
                    >
                      <div className="flex-1 min-w-0">
                        <Label className="text-sm font-medium">{item.label}</Label>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={profileData.notifications[item.key]}
                        onCheckedChange={(checked) =>
                          setProfileData(prev => ({
                            ...prev,
                            notifications: { ...prev.notifications, [item.key]: checked },
                          }))
                        }
                        data-testid={`switch-notification-${item.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 5 && (
              <div className="text-center space-y-4" data-testid="step-complete">
                <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle className="text-xl">You're all set!</CardTitle>
                <CardDescription className="text-base">
                  Your supporter profile is ready. Here's what you can do next:
                </CardDescription>
                <div className="space-y-2 text-left bg-muted/50 rounded-lg p-4">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-primary flex-shrink-0" />
                      Browse and contribute to community health funds
                    </li>
                    <li className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-primary flex-shrink-0" />
                      Explore condition-based wallets and join as a member
                    </li>
                    <li className="flex items-center gap-2">
                      <HandHeart className="w-4 h-4 text-primary flex-shrink-0" />
                      Share referral links to grow the community
                    </li>
                  </ul>
                </div>
                {profileData.supportInterests.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Your interests:</p>
                    <div className="flex flex-wrap justify-center gap-1">
                      {profileData.supportInterests.map(slug => {
                        const cond = conditions.find(c => c.slug === slug);
                        return cond ? (
                          <Badge key={slug} variant="secondary" className="text-xs">
                            {cond.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t">
              {currentStep > 0 && currentStep < 5 ? (
                <Button variant="ghost" onClick={handleBack} data-testid="button-onboarding-back">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              ) : (
                <div />
              )}
              {currentStep === 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <Button variant="ghost" onClick={handleSkip} data-testid="button-onboarding-skip">
                    Skip for now
                  </Button>
                  <Button onClick={handleNext} data-testid="button-onboarding-next">
                    Get started
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
              {currentStep > 0 && currentStep < 5 && (
                <Button onClick={handleNext} data-testid="button-onboarding-next">
                  Continue
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {currentStep === 5 && (
                <Button
                  onClick={handleComplete}
                  className="w-full"
                  disabled={updateProfileMutation.isPending}
                  data-testid="button-onboarding-complete"
                >
                  {updateProfileMutation.isPending ? "Saving..." : "Start exploring"}
                  {!updateProfileMutation.isPending && <ArrowRight className="w-4 h-4 ml-1" />}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
